<template>
  <Logo v-if="store.view !== AppView.VIDEO" class="fixed left-4 top-3 z-20 lg:left-10 lg:top-8" />
  <ClientOnly>
    <Transition name="slideX">
      <MenuBackDrop v-show="isMenuBackDropVisible" class="z-10" />
    </Transition>
  </ClientOnly>
  <Transition name="menu">
    <MenuList
      v-if="isMenuVisible && store.view !== AppView.VIDEO"
      class="pointer-events-none z-10"
      :class="{ 'text-white mix-blend-difference invert': isIntroScene, 'lg:!fixed': isHomePinned }"
      :data="store.allMenuItems"
      :invert-colors="isMenuInvertColors"
    />
  </Transition>
  <Transition name="button" class="delay-200">
    <MenuCTA
      v-if="isMenuVisible && store.view !== AppView.VIDEO"
      class="z-10"
      :data="store.allMenuCtas"
    />
  </Transition>
  <MenuButton v-if="store.view !== AppView.VIDEO" class="z-10" />
  <main class="flow-root min-h-screen">
    <NuxtPage
      :transition="{
        name: 'page',
        mode: 'out-in'
      }"
    />
  </main>
  <Footer class="relative" :data="store" />
  <LazyCookieBanner v-if="loadCookieBanner" />
  <LazyClientOnly v-if="config.public.env === 'preview'">
    <DatoDraftMode />
  </LazyClientOnly>
</template>

<script setup lang="ts">
import '~/assets/css/transitions.css'

import { toHead } from 'vue-datocms'
import { useAppStore } from '~/stores/AppStore'
import { AppView } from '~/types'
import { useSchemaGraph, buildSchemaOrganization, buildSchemaWebSite } from '~/composables/schema'
import { SCHEMA_TEXT, SCHEMA_SAME_AS, schemaIds } from '~/types/schema'

const config = useRuntimeConfig()
const appConfig = useAppConfig()
const store = useAppStore()
const route = useRoute()

const canonicalUrl = computed(() => {
  const base = config.public.appUrl.replace(/\/$/, '')
  return `${base}${route.path}`
})

store.fetchData()

const { registerReactive, useSchemaHead } = useSchemaGraph()
useSchemaHead()

registerReactive('global', () => {
  const ids = schemaIds(config.public.appUrl)
  return [
    buildSchemaOrganization({
      id: ids.ORG,
      name: SCHEMA_TEXT.ORG_NAME,
      url: config.public.appUrl,
      sameAs: [...SCHEMA_SAME_AS]
    }),
    buildSchemaWebSite({
      id: ids.WEBSITE,
      name: SCHEMA_TEXT.ORG_NAME,
      url: config.public.appUrl,
      inLanguage: 'en',
      publisher: { '@id': ids.ORG }
    })
  ]
})

const { width } = useWindowSize()

const isMenuVisible = useState('menu')
const isMenuBackDropVisible = useState('menuBackDrop')
const isMenuInvertColors = useState('menuInvertColors') as unknown as boolean
const isIntroScene = useState('intro')
const isHomePinned = useState('homePin')
const { showOnlyMenu, handleResize } = useMenu()

// Delay loading of cookie banner
const loadCookieBanner = ref(false)

onMounted(() => {
  useLenis()
  if (width.value >= 1024 && !isMenuVisible.value) {
    showOnlyMenu()
  }

  useTimeoutFn(() => {
    loadCookieBanner.value = true
  }, 3000)

  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

useHead({
  htmlAttrs: {
    lang: 'en'
  },
  bodyAttrs: {
    class: computed(() =>
      store.theme === AppTheme.LIGHT
        ? 'bg-white text-black border-black antialiased'
        : store.theme === AppTheme.DARK
          ? 'bg-black text-white border-white antialiased'
          : 'antialiased'
    )
  },
  link: [
    { rel: 'preconnect', href: 'https://www.datocms-assets.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com' }
  ],
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${titleChunk}` : appConfig.title
  }
})

useHead(() => {
  return toHead(store._site?.faviconMetaTags || [])
})

useSeoMeta({
  ogUrl: () => canonicalUrl.value
})
</script>

<style>
body {
  transition: background-color 500ms linear;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 500ms cubic-bezier(0.37, 0, 0.63, 1);
}

.page-enter-active {
  transition-delay: 100ms;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

/* Hide scrollbar for Chrome, Safari and Opera */
::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
body {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
